/**
 * Régimen fiscal SAT aceptado por `POST …/company/data` (`tax_regime_code`).
 *
 * El campo es OPCIONAL, pero desde el 2026-08-27 el backend lo VALIDA cuando
 * viene: antes escribía lo que llegara. Un código exclusivo de persona física
 * responde `ONB_COMPANY_002`.
 *
 * Esto importa más de lo que parece porque el régimen **no se pinta**: salió
 * del diseño del 2026-08-18 y hoy lo llena el OCR de la CSF. Si el OCR lee un
 * código de PF, el usuario recibe un error en un paso temprano y **no tiene
 * dónde corregirlo**. Por eso el adapter omite el código cuando no está en
 * esta lista: el contrato acepta la ausencia, no el valor inválido.
 *
 * Los códigos y sus nombres son del SAT: no se renombran ni se reordenan.
 */
export interface PmTaxRegime {
  code: string;
  label: string;
  /** `moral` sólo aplica a PM; `ambos` también existe para persona física. */
  scope: 'moral' | 'ambos';
  /**
   * Derogado por el SAT. Se acepta para poder editar expedientes históricos,
   * pero NO debe ofrecerse en capturas nuevas.
   */
  deprecated?: boolean;
}

export const PM_TAX_REGIMES: readonly PmTaxRegime[] = [
  { code: '601', label: 'General de Ley Personas Morales', scope: 'moral' },
  { code: '603', label: 'Personas Morales con Fines no Lucrativos', scope: 'moral' },
  { code: '609', label: 'Consolidación', scope: 'moral', deprecated: true },
  {
    code: '610',
    label: 'Residentes en el Extranjero sin Establecimiento Permanente',
    scope: 'ambos',
  },
  { code: '616', label: 'Sin obligaciones fiscales', scope: 'ambos' },
  {
    code: '620',
    label: 'Sociedades Cooperativas de Producción que difieren ingresos',
    scope: 'moral',
  },
  {
    code: '622',
    label: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras',
    scope: 'ambos',
  },
  { code: '623', label: 'Opcional para Grupos de Sociedades', scope: 'moral' },
  { code: '624', label: 'Coordinados', scope: 'moral' },
  { code: '626', label: 'Régimen Simplificado de Confianza', scope: 'ambos' },
] as const;

/**
 * Códigos que el backend RECHAZA con `ONB_COMPANY_002` por ser exclusivos de
 * persona física. Se listan explícitamente —en vez de derivarlos por descarte—
 * para poder distinguir "régimen de PF" de "código que no existe".
 */
export const PF_ONLY_TAX_REGIMES = [
  '605',
  '606',
  '607',
  '608',
  '611',
  '612',
  '614',
  '615',
  '621',
  '625',
] as const;

/** ¿El backend aceptaría este `tax_regime_code` para una PM? */
export function isPmTaxRegime(code: string | undefined | null): boolean {
  if (!code) return false;
  const normalized = code.trim();
  return PM_TAX_REGIMES.some(regime => regime.code === normalized);
}

/**
 * Los que sí se ofrecen en una captura nueva: los aceptados menos los
 * derogados por el SAT.
 */
export const PM_TAX_REGIMES_SELECTABLE = PM_TAX_REGIMES.filter(regime => !regime.deprecated);
